/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { LoadingSpinner } from "../../LoadingSpinner";
import { toast } from "react-toastify";
import "./index.scss";
import login from "../../../Assests/Images/login.svg";
import Logo from "../../../Assests/Images/Logo.svg";
import Microsoft from "../../../Assests/Images/Microsoft.svg";
import alertIcon from "../../../Assests/Images/alert.svg";
import CustomModal from "../../Common/Modal";
import {
  getEmailFromIdToken,
  getStoredIdToken,
  getTokenExpiryFromJwt,
  persistIdToken,
  clearStoredIdToken,
} from "../../../lib/tokenUtils";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import {
  setAuthorisedUser,
  setEmployeeTransactionData,
  setIsAuthorised,
  setPartialI9SelectedCard,
} from "../../../Store/reducers/AppReducer";
import { clearApiError } from "../../../Store/reducers/APIReducer";
import api from "../../../utils/apiUtils/apiUtils";
import { getBaseUrl } from "../../../utils/apiUtils/BaseURL";
import {
  resolvePrimaryRole,
  resolveRoleForDestination,
  resolveAuthorisedUser,
  setCachedUserProfile,
} from "../../../utils/userUtils";
import {
  isSignInWithEmailLink,
  onAuthStateChanged,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signInWithEmailLink,
  signOut,
} from "firebase/auth";
import { auth } from "../../../lib/firebase";
import {
  getFreshIdToken,
  handleGoogleRedirectResult,
  signInWithGooglePopupSmart,
  signInWithGoogleRedirect,
  signInWithMicrosoftPopupSmart,
  signInWithMicrosoftRedirect,
  firebaseSignOut,
} from "../../../lib/firebaseAuth";

const COUNTRIES = [
  { code: "US", dialCode: "+1", name: "United States" },
  { code: "CA", dialCode: "+1", name: "Canada" },
];

const PHONE_RULES = {
  US: { min: 10, max: 10 },
  CA: { min: 10, max: 10 },
};

const AUTH_MESSAGES = {
  "auth/code-expired": "That code has expired. Please request a new one.",
  "auth/invalid-verification-code": "That code isn't correct. Please check it and try again.",
  "auth/missing-verification-code": "Please enter the code we sent you.",
  "auth/invalid-phone-number": "Please enter a valid phone number.",
  "auth/missing-phone-number": "Please enter your phone number.",
  "auth/operation-not-allowed": "Phone sign-in isn't available at the moment. Please use another sign-in option or contact support.",
  "auth/too-many-requests": "Too many attempts. Please wait a few minutes and try again.",
  "auth/quota-exceeded": "We can't send any more codes right now. Please try again later.",
  "auth/network-request-failed": "We couldn't reach the server. Check your connection and try again.",
  "auth/user-disabled": "This account has been disabled. Please contact your administrator.",
  "auth/invalid-email": "Please enter a valid email address.",
  "auth/user-not-found": "No account was found for those details.",
  "auth/popup-closed-by-user": "Popup Closed By User",
  "auth/cancelled-popup-request": "Popup Closed By User",
  "auth/popup-blocked": "Popup Closed By User",
  "auth/error-code:-47": "Access not found.",
  "-47": "Access not found.",
};

const GENERIC_AUTH_ERROR = "We couldn't sign you in. Please try again.";

const friendlyAuthMessage = (code, rawMessage) => {
  const text = typeof rawMessage === "string" ? rawMessage : "";

  if (code && AUTH_MESSAGES[code]) return AUTH_MESSAGES[code];

  const found = Object.keys(AUTH_MESSAGES).find((key) => text.includes(key));
  if (found) return AUTH_MESSAGES[found];

  if (/firebase|auth\//i.test(text)) return GENERIC_AUTH_ERROR;

  return text || GENERIC_AUTH_ERROR;
};

const SuperAdminLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [ssoTried, setSsoTried] = useState(false);
  const [accountConflict, setAccountConflict] = useState(null);
  const [redirectProcessing, setRedirectProcessing] = useState(
    () => sessionStorage.getItem("sso_redirect_pending") === "1"
  );
  const [emailLinkInProgress, setEmailLinkInProgress] = useState(false);
  const [emailLinkError, setEmailLinkError] = useState("");
  const [, setRedirectAuthError] = useState("");
  const redirectKicked = useRef(false);
  const lastAuthKey = useRef(null);
  const emailLinkContext = useRef({ isEmailLink: false, redirectUrl: "" });
  const suppressAutoResumeRef = useRef(false);
  const checkUserAccessQueueRef = useRef(Promise.resolve());

  const [phoneAuthOpen, setPhoneAuthOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(
    COUNTRIES.find((c) => c.code === "US")
  );
  const [localPhone, setLocalPhone] = useState("");
  const [countrySearch, setCountrySearch] = useState("");
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef(null);
  const [otpCode, setOtpCode] = useState("");
  const [phoneAuthStep, setPhoneAuthStep] = useState("idle");
  const [phoneAuthError, setPhoneAuthError] = useState("");
  const [phoneAuthLoading, setPhoneAuthLoading] = useState(false);
  const [continueSubmitting, setContinueSubmitting] = useState(false);
  const recaptchaRef = useRef(null);
  const confirmationRef = useRef(null);
  const [emailOtpOpen, setEmailOtpOpen] = useState(false);
  const [emailOtpRequestedEmail, setEmailOtpRequestedEmail] = useState("");
  const [emailOtpCode, setEmailOtpCode] = useState("");
  const [emailOtpLoading, setEmailOtpLoading] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [emailOtpError, setEmailOtpError] = useState("");
  const emailOtpRefs = useRef([]);

  const phoneRule = PHONE_RULES[selectedCountry?.code] ?? { min: 7, max: 15 };
  const fullPhoneNumber = `${selectedCountry.dialCode}${localPhone.replace(/\D/g, "")}`;

  const filteredCountries = COUNTRIES.filter(
    (c) =>
      c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
      c.dialCode.includes(countrySearch)
  );

  const reportAuthError = (message, code) => {
    const finalMessage = friendlyAuthMessage(code, message);
    setRedirectAuthError(finalMessage);
    toast.error(finalMessage, { toastId: "auth-error" });
  };

  const getSsoProviderForEmail = async (email) => {
    const domain = (email.split("@")[1] || "").toLowerCase().trim();
    if (!domain) return "unknown";

    if (domain === "gmail.com" || domain.endsWith(".googlemail.com")) return "google";
    if (["outlook.com", "hotmail.com", "live.com", "msn.com"].includes(domain)) return "microsoft";

    try {
      const res = await fetch(
        `https://dns.google/resolve?name=${encodeURIComponent(domain)}&type=MX`
      );
      if (!res.ok) return "unknown";
      const data = await res.json().catch(() => null);
      const records = Array.isArray(data?.Answer)
        ? data.Answer.map((a) => String(a?.data || "").toLowerCase()).join(" ")
        : "";
      if (!records) return "unknown";
      if (records.includes("google.com")) return "google";
      if (
        records.includes("outlook.com") ||
        records.includes("protection.outlook.com") ||
        records.includes("microsoft.com") ||
        records.includes("office365.com") ||
        records.includes("onmicrosoft.com")
      ) return "microsoft";
    } catch {
    }
    return "unknown";
  };

  const [urlLoginHint] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const hint = params.get("login_hint") || "";
    if (hint) window.history.replaceState({}, document.title, window.location.pathname);
    return hint;
  });

  const [autoLoginHint] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem("lastLoginHint") || "";
  });

  const [loginHint, setLoginHint] = useState(autoLoginHint);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorCode = params.get("error");
    if (!errorCode) return;
    window.history.replaceState({}, document.title, window.location.pathname);
    const ssoErrorMessages = {
      sso: "Something went wrong starting sign-in. Please try again.",
      ssoinvalid: "Your sign-in session expired or is invalid. Please sign in again.",
      unverified: "Please verify your email before continuing.",
      noaccount: "No I-9 account was found for this email. Contact your administrator.",
      norole: "Your account doesn't have an assigned role in I-9. Contact your administrator.",
    };
    reportAuthError(ssoErrorMessages[errorCode] || "Unable to sign you in. Please try again.");
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(e.target)) {
        setCountryDropdownOpen(false);
        setCountrySearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const redirectPending = sessionStorage.getItem("sso_redirect_pending") === "1";
    if (redirectPending) {
      setSsoTried(true);
      handleGoogleRedirectResult()
        .then(() => {
          sessionStorage.removeItem("sso_redirect_pending");
          setRedirectProcessing(false);
        })
        .catch((err) => {
          sessionStorage.removeItem("sso_redirect_pending");
          setRedirectProcessing(false);
          reportAuthError(
            err?.message || err?.code || "Google sign-in failed. Please try again.",
            err?.code
          );
        });
    } else {
      setRedirectProcessing(false);
      handleGoogleRedirectResult().catch((err) => {
        reportAuthError(
          err?.message || err?.code || "Google sign-in failed. Please try again.",
          err?.code
        );
      });
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isEmailLink = isSignInWithEmailLink(auth, window.location.href);
    let redirectUrl = params.get("redirect_url") || "";
    let emailFromQuery = params.get("email") || "";

    if (!redirectUrl || !emailFromQuery) {
      const raw = window.location.search.startsWith("?")
        ? window.location.search.slice(1)
        : window.location.search;
      if (raw && (raw.includes("%3D") || raw.includes("%26"))) {
        try {
          const decoded = decodeURIComponent(raw);
          const nestedParams = new URLSearchParams(decoded);
          redirectUrl = redirectUrl || nestedParams.get("redirect_url") || "";
          emailFromQuery = emailFromQuery || nestedParams.get("email") || "";
        } catch {
        }
      }
    }
    emailLinkContext.current = { isEmailLink, redirectUrl };

    if (!isEmailLink) return;
    if (redirectUrl) {
      sessionStorage.setItem(
        "email_link_pending_redirect",
        JSON.stringify({ url: redirectUrl, email: (emailFromQuery || "").toLowerCase() })
      );
    }

    const email = emailFromQuery || window.localStorage.getItem("emailForSignIn");
    if (!email) {
      setEmailLinkError("Missing email for email-link sign-in.");
      return;
    }

    setEmailLinkInProgress(true);
    signInWithEmailLink(auth, email, window.location.href)
      .then(() => {
        window.localStorage.removeItem("emailForSignIn");
        window.history.replaceState({}, document.title, window.location.pathname);
      })
      .catch(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      })
      .finally(() => {
        setEmailLinkInProgress(false);
      });
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setAuthUser(user);
        setAuthReady(true);
        return;
      }
      if (suppressAutoResumeRef.current) {
        setAuthUser(null);
        setAuthReady(true);
        return;
      }
      const token = getStoredIdToken();
      const expiry = getTokenExpiryFromJwt(token);
      const validToken = Boolean(token && (!expiry || expiry > Date.now()));
      if (validToken) {
        const emailFromToken = getEmailFromIdToken(token);
        setAuthUser(emailFromToken ? { email: emailFromToken } : { tokenUser: true });
      } else {
        setAuthUser(null);
      }
      setAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!phoneAuthOpen) return;
    setupRecaptcha().catch((err) => {
      console.error("Failed to initialize reCAPTCHA on open.", err);
    });
  }, [phoneAuthOpen]);

  useEffect(() => {
    if (!emailOtpOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusTimer = setTimeout(() => {
      emailOtpRefs.current[0]?.focus();
    }, 0);
    return () => {
      clearTimeout(focusTimer);
      document.body.style.overflow = prevOverflow;
    };
  }, [emailOtpOpen]);

  useEffect(() => {
    const run = async () => {
      if (!authReady) return;
      if (ssoTried) return;
      if (suppressAutoResumeRef.current) return;
      if (emailLinkContext.current.isEmailLink) return;

      if (urlLoginHint && authUser?.email &&
          authUser.email.toLowerCase() !== urlLoginHint.toLowerCase()) {
        clearStoredIdToken();
        await signOut(auth);
        return;
      }

      if (authUser) return;

      try {
        const token = getStoredIdToken();
        const expiry = getTokenExpiryFromJwt(token);
        const validToken = Boolean(token && (!expiry || expiry > Date.now()));
        if (validToken) {
          const emailFromToken = getEmailFromIdToken(token);
          setAuthUser(emailFromToken ? { email: emailFromToken } : { tokenUser: true });
          setSsoTried(true);
          return;
        }
      } catch {
      }

      if (urlLoginHint) {
        setLoginHint(urlLoginHint);
        setSsoTried(true);
        return;
      }

      const hint = autoLoginHint;
      if (!hint) {
        setSsoTried(true);
        return;
      }

      if (!redirectKicked.current) {
        redirectKicked.current = true;
        try {
          sessionStorage.setItem("sso_redirect_pending", "1");
          await signInWithGoogleRedirect(hint);
        } catch {
          sessionStorage.removeItem("sso_redirect_pending");
          setSsoTried(true);
        }
      }
    };
    run();
  }, [urlLoginHint, autoLoginHint, ssoTried, authReady, authUser]);


  useEffect(() => {
    if (!authUser) {
      lastAuthKey.current = null;
      setCurrentUser(null);
      return;
    }
    if (accountConflict || suppressAutoResumeRef.current) return;
    if (emailLinkContext.current.isEmailLink && emailLinkInProgress) return;
    if (urlLoginHint && authUser?.email &&
        authUser.email.toLowerCase() !== urlLoginHint.toLowerCase()) {
      return;
    }
    const authKey =
      authUser?.uid ||
      authUser?.email ||
      (authUser?.tokenUser ? "tokenUser" : "user");
    if (lastAuthKey.current === authKey) return;
    lastAuthKey.current = authKey;
    checkUserAccess();
  }, [authUser, urlLoginHint, accountConflict, emailLinkInProgress]);

  const finalizeAuthorisedUser = (authorisedUser, emailHint) => {
    setCachedUserProfile(authorisedUser);

    setCurrentUser(authorisedUser);
    dispatch(setIsAuthorised(true));
    dispatch(setAuthorisedUser(authorisedUser));
    dispatch(clearApiError(null));

    if (emailHint) {
      localStorage.setItem("lastUpn", emailHint);
      localStorage.setItem("lastLoginHint", emailHint);
    }
  };

  const checkUserAccess = () => {
    const run = async () => {
      try {
        const previousToken = getStoredIdToken();
        const previousExpiry = getTokenExpiryFromJwt(previousToken);
        const previousValid = Boolean(
          previousToken && (!previousExpiry || previousExpiry > Date.now())
        );
        const previousEmail = previousValid
          ? (getEmailFromIdToken(previousToken) || "").toLowerCase()
          : "";

        let accessToken = await getFreshIdToken(false);
        if (!accessToken) accessToken = getStoredIdToken();
        if (!accessToken) throw new Error("Failed to get Firebase token");

        const newEmail = (
          authUser?.email ||
          getEmailFromIdToken(accessToken) ||
          ""
        ).toLowerCase();

        if (previousEmail && newEmail && previousEmail !== newEmail) {
          suppressAutoResumeRef.current = true;
          sessionStorage.removeItem("pending_redirect_url");
          sessionStorage.removeItem("email_link_pending_redirect");
          await firebaseSignOut().catch(() => {});
          if (previousValid) persistIdToken(previousToken);
          setAuthUser(null);
          setCurrentUser(null);
          setSsoTried(true);
          setAccountConflict({ existingEmail: previousEmail });
          return;
        }

        persistIdToken(accessToken);

        const parseJwtPayload = (token) => {
          try {
            const [, payload] = token.split(".");
            if (!payload) return null;
            return JSON.parse(atob(payload));
          } catch {
            return null;
          }
        };

        const maybeBindPhone = async () => {
          const payload = parseJwtPayload(accessToken);
          const pendingPhone = payload?.pending_phone;
          if (!pendingPhone) return;
          if (authUser?.phoneNumber) return;
          try {
            const response = await fetch(
              "https://us-central1-canary-ai-app.cloudfunctions.net/manual_bind_phone",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ phoneNumber: pendingPhone }),
              }
            );
            if (response.ok) {
              await authUser?.reload?.();
              await authUser?.getIdToken?.(true);
            }
          } catch {
          }
        };

        if (authUser?.phoneNumber && authUser?.reload) await maybeBindPhone();

        const emailHint = authUser?.email || getEmailFromIdToken(accessToken) || "";
        if (emailHint) {
          const allowed = await checkI9Access(emailHint);
          if (!allowed) {
            sessionStorage.removeItem("pending_redirect_url");
            sessionStorage.removeItem("email_link_pending_redirect");
            if (window.location.search.includes("redirect_url")) {
              window.history.replaceState({}, document.title, window.location.pathname);
            }
            clearStoredIdToken();
            await firebaseSignOut().catch(() => {});
            setCurrentUser(null);
            setAuthUser(null);
            setSsoTried(true);
            reportAuthError("Access not found.");
            return;
          }
        }

        api.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;

        const { profile: authorisedUser } = await resolveAuthorisedUser({
          email: emailHint,
          forceRefresh: true,
        });
        if (suppressAutoResumeRef.current) return;

        finalizeAuthorisedUser(authorisedUser, emailHint);
      } catch {
        sessionStorage.removeItem("pending_redirect_url");
        sessionStorage.removeItem("email_link_pending_redirect");
        if (window.location.search.includes("redirect_url")) {
          window.history.replaceState({}, document.title, window.location.pathname);
        }
        clearStoredIdToken();
        await firebaseSignOut().catch(() => {});
        setCurrentUser(null);
        setAuthUser(null);
        setSsoTried(true);
        reportAuthError("Unable to verify access. Please try again.");
      }
    };
    const next = checkUserAccessQueueRef.current.then(run, run);
    checkUserAccessQueueRef.current = next.catch(() => {});
    return next;
  };

  useEffect(() => {
    if (!currentUser) return;
    const currentUserEmail = (
      window.localStorage.getItem("lastLoginHint") ||
      currentUser?.email ||
      ""
    ).toLowerCase();
    let emailLinkRedirect = "";
    const rawEmailLinkRedirect = sessionStorage.getItem("email_link_pending_redirect");
    if (rawEmailLinkRedirect) {
      try {
        const parsed = JSON.parse(rawEmailLinkRedirect);
        if (!parsed?.email || parsed.email === currentUserEmail) {
          emailLinkRedirect = parsed?.url || "";
          sessionStorage.removeItem("email_link_pending_redirect");
        }
      } catch {
        sessionStorage.removeItem("email_link_pending_redirect");
      }
    }

    const pendingRedirectRaw =
      emailLinkRedirect || sessionStorage.getItem("pending_redirect_url");
    const isUselessRedirect = (() => {
      if (!pendingRedirectRaw) return true;
      try {
        const url = new URL(pendingRedirectRaw, window.location.origin);
        return (
          url.origin === window.location.origin &&
          url.pathname === window.location.pathname &&
          !url.search &&
          !url.hash
        );
      } catch {
        return false;
      }
    })();
    const pendingRedirect = isUselessRedirect ? "" : pendingRedirectRaw;
    if (isUselessRedirect) {
      sessionStorage.removeItem("pending_redirect_url");
    }
    if (pendingRedirect) {
      sessionStorage.removeItem("pending_redirect_url");

      let destinationPath = pendingRedirect;
      let destinationTransactionId = null;
      try {
        const parsed = new URL(pendingRedirect, window.location.origin);
        destinationPath = parsed.pathname;
        destinationTransactionId = parsed.searchParams.get("transactionId");
      } catch {
      }
      const redirectRole = resolveRoleForDestination(
        currentUser?.roles,
        destinationPath,
        destinationTransactionId
      );

      if (redirectRole?.role === 3) {
        sessionStorage.setItem("selectedAdminCompanyId", redirectRole?.companyId);
        dispatch(setPartialI9SelectedCard(null));
        dispatch(
          setEmployeeTransactionData({
            userId: redirectRole?.userId ?? currentUser?.id,
            transactionId: redirectRole?.transactionId,
          })
        );
      } else if ([1, 2].includes(redirectRole?.role)) {
        sessionStorage.setItem("selectedAdminCompanyId", redirectRole?.companyId);
      }
      try {
        const url = new URL(pendingRedirect, window.location.origin);
        if (url.origin === window.location.origin) {
          navigate(url.pathname + url.search + url.hash);
        } else {
          window.location.href = pendingRedirect;
        }
      } catch {
        navigate(pendingRedirect);
      }
      return;
    }
    const primaryRole = resolvePrimaryRole(currentUser?.roles);
    if (currentUser.isSuperAdmin) {
      navigate("/companyadmin");
    } else if ([1, 2].includes(primaryRole?.role)) {
      sessionStorage.setItem("selectedAdminCompanyId", primaryRole?.companyId);
      navigate("/companyadmin");
    } else if ([3].includes(primaryRole?.role)) {
      sessionStorage.setItem("selectedAdminCompanyId", primaryRole?.companyId);
      dispatch(setPartialI9SelectedCard(null));
      dispatch(
        setEmployeeTransactionData({
          userId: currentUser?.id,
          transactionId: primaryRole?.transactionId,
        })
      );
      navigate("/employee/launch");
    } else {
      navigate("/");
    }
  }, [currentUser, dispatch, navigate]);

  const stuckRetryTimerRef = useRef(null);
  const stuckReloadTimerRef = useRef(null);
  useEffect(() => {
    const isStuck = authReady && Boolean(authUser) && !currentUser;
    if (!isStuck) return;

    stuckRetryTimerRef.current = setTimeout(() => {
      lastAuthKey.current = null; 
      checkUserAccess();
    }, 3000);
    stuckReloadTimerRef.current = setTimeout(() => {
      window.location.reload();
    }, 8000);

    return () => {
      clearTimeout(stuckRetryTimerRef.current);
      clearTimeout(stuckReloadTimerRef.current);
    };
  }, [authReady, authUser, currentUser]);

  const handleLogin = async () => {
    const email =
      (loginHint || "").trim() ||
      (authUser?.email || "").trim() ||
      window.localStorage.getItem("lastLoginHint") ||
      "";
    if (!email) return;
    if (continueSubmitting) return;

    suppressAutoResumeRef.current = false;
    setContinueSubmitting(true);
    setRedirectAuthError("");
    window.localStorage.setItem("emailForSignIn", email);

    try {
      const allowed = await checkI9Access(email);
      if (!allowed) return;
      const provider = await getSsoProviderForEmail(email);
      if (provider === "google") {
        await signInWithGooglePopupSmart(email);
        return;
      }
      if (provider === "microsoft") {
        await signInWithMicrosoftPopupSmart(email);
        return;
      }
      await sendEmailOtp(email);
    } catch (err) {
      reportAuthError(err?.message || err?.code || "Unable to verify user. Please try again.");
    } finally {
      setContinueSubmitting(false);
    }
  };

  const handleSocalLogin = () => {
    suppressAutoResumeRef.current = false;
    signInWithGooglePopupSmart(undefined, { prompt: "select_account" })
      .then(async (result) => {
        const popupEmail = result?.user?.email || auth.currentUser?.email || "";
        if (!popupEmail) { reportAuthError("Unable to read Google account email."); return; }
        const allowed = await checkI9Access(popupEmail);
        if (!allowed) { await firebaseSignOut(); return; }
        return result;
      })
      .catch((err) => {
        const code = err?.code || "";
        if (
          code === "auth/popup-closed-by-user" ||
          code === "auth/cancelled-popup-request" ||
          code === "auth/popup-blocked"
        ) {
          reportAuthError("Popup Closed By User", code);
        } else {
          reportAuthError(err?.message || err?.code || "Google sign-in failed. Please try again.", code);
        }
      });
  };

  const handleMicrosoftLogin = (loginType) => {
    suppressAutoResumeRef.current = false;
    if (loginType === "popup") {
      signInWithMicrosoftPopupSmart(undefined, { prompt: "select_account" })
        .then(async (result) => {
          const popupEmail = result?.user?.email || auth.currentUser?.email || "";
          if (!popupEmail) { reportAuthError("Unable to read Microsoft account email."); return; }
          const allowed = await checkI9Access(popupEmail);
          if (!allowed) { await firebaseSignOut(); return; }
          return result;
        })
        .catch((err) => {
          const code = err?.code || "";
          if (
            code === "auth/popup-closed-by-user" ||
            code === "auth/cancelled-popup-request" ||
            code === "auth/popup-blocked"
          ) {
            reportAuthError("Popup Closed By User", code);
          } else {
            reportAuthError(err?.message || err?.code || "Microsoft sign-in failed. Please try again.", code);
          }
        });
      return;
    }
    signInWithMicrosoftRedirect();
  };

  const checkI9Access = async (email) => {
    try {
      const encoded = encodeURIComponent(email.trim());
      const res = await fetch(
        `${getBaseUrl()}/user/checki9useraccess?email=${encoded}`,
        { method: "GET", headers: { accept: "application/json" } }
      );
      if (!res.ok) { reportAuthError("Access not found for this email."); return false; }
      const json = await res.json().catch(() => null);
      const exists = json?.result?.exists === true;
      if (!exists) { reportAuthError("Access not found for this email."); return false; }
      return true;
    } catch {
      reportAuthError("Unable to verify access. Please try again.");
      return false;
    }
  };

  const setupRecaptcha = async () => {
    const container = document.getElementById("recaptcha-container");
    if (!container) throw new Error("reCAPTCHA container not found.");
    if (recaptchaRef.current) return recaptchaRef.current;

    recaptchaRef.current = new RecaptchaVerifier(auth, "recaptcha-container", {
      size: "invisible",
      callback: () => {},
      "expired-callback": () => {},
    });
    await recaptchaRef.current.render();
    window.recaptchaVerifier = recaptchaRef.current;
    return recaptchaRef.current;
  };

  const handleSendOtp = async () => {
    const digits = localPhone.replace(/\D/g, "");
    if (!digits) {
      setPhoneAuthError("Enter a valid phone number.");
      return;
    }
    setPhoneAuthError("");
    setPhoneAuthLoading(true);
    try {
      const verifier = await setupRecaptcha();
      const confirmation = await signInWithPhoneNumber(auth, fullPhoneNumber, verifier);
      confirmationRef.current = confirmation;
      window.confirmationResult = confirmation;
      setPhoneAuthStep("code-sent");
    } catch (err) {
      setPhoneAuthError(friendlyAuthMessage(err?.code, err?.message));
      try { if (recaptchaRef.current) recaptchaRef.current.clear(); } catch {}
      recaptchaRef.current = null;
    } finally {
      setPhoneAuthLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode.trim()) { setPhoneAuthError("Enter the 6-digit OTP."); return; }
    if (!confirmationRef.current) {
      setPhoneAuthError("OTP session expired. Please request a new code.");
      return;
    }
    suppressAutoResumeRef.current = false;
    setPhoneAuthError("");
    setPhoneAuthLoading(true);
    try {
      await confirmationRef.current.confirm(otpCode.trim());
      setOtpCode("");
      setPhoneAuthStep("idle");
    } catch (err) {
      const code = err?.code || "";
      const message = err?.message || "";
      const isAccessError =
        code === "auth/error-code:-47" ||
        code === "-47" ||
        message.includes("auth/error-code:-47");
      if (isAccessError) {
        reportAuthError("Access not found.", code);
      } else {
        reportAuthError(message || "Invalid OTP. Please try again.", code);
      }
      setPhoneAuthError("");
    } finally {
      setPhoneAuthLoading(false);
    }
  };

  const sendEmailOtp = async (email) => {
    if (!email) return;
    setEmailOtpLoading(true);
    setEmailOtpError("");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_AUTH_API_BASE_URL}/api/v1/auth/send-otp`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        }
      );
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || "Failed to send OTP.");
      }
      setEmailOtpRequestedEmail(email);
      setEmailOtpOpen(true);
      setEmailOtpCode("");
      toast.success(data?.message || "OTP sent successfully.");
    } catch (err) {
      const message = err?.message || "Failed to send OTP.";
      setEmailOtpError(message);
      toast.error(message);
    } finally {
      setEmailOtpLoading(false);
    }
  };

  const verifyEmailOtp = async () => {
    if (!emailOtpRequestedEmail) return;
    if (!emailOtpCode.trim()) {
      setEmailOtpError("Enter the 4-digit OTP.");
      return;
    }
    suppressAutoResumeRef.current = false;
    setEmailOtpLoading(true);
    setIsVerifyingOtp(true);
    setEmailOtpError("");
    let succeeded = false;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_AUTH_API_BASE_URL}/api/v1/auth/verify-otp`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: emailOtpRequestedEmail,
            otp: emailOtpCode.trim(),
            application_name: "i9",
          }),
        }
      );
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || "OTP verification failed.");
      }
      const redirectUrl =
        data?.sign_in_link || data?.redirecturl || data?.redirectUrl;
      if (redirectUrl) {
        succeeded = true;
        window.location.href = redirectUrl;
        return;
      }
      toast.success(data?.message || "OTP verified successfully.");
    } catch (err) {
      const message = err?.message || "OTP verification failed.";
      setEmailOtpError(message);
      toast.error(message);
    } finally {
      setEmailOtpLoading(false);
      if (!succeeded) setIsVerifyingOtp(false);
    }
  };

  const handleEmailOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = emailOtpCode.split("");
    next[index] = digit;
    const merged = next.join("").slice(0, 4);
    setEmailOtpCode(merged);
    if (digit && emailOtpRefs.current[index + 1]) {
      emailOtpRefs.current[index + 1]?.focus();
    }
  };

  const handleEmailOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !emailOtpCode[index] && emailOtpRefs.current[index - 1]) {
      emailOtpRefs.current[index - 1]?.focus();
    }
  };

  const handleEmailOtpPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (!pasted) return;
    setEmailOtpCode(pasted);
    const nextIndex = Math.min(pasted.length, 3);
    emailOtpRefs.current[nextIndex]?.focus();
  };

  if (emailLinkInProgress && !accountConflict) {
    return <LoadingSpinner message="Completing email sign-in..." />;
  }

  if (authReady && authUser && !accountConflict) {
    return <LoadingSpinner message="Attempting automatic sign-in..." />;
  }

  if ((!authReady || redirectProcessing) && !accountConflict) {
    return (
      <LoadingSpinner
        message={
          urlLoginHint || redirectProcessing || emailLinkContext.current.isEmailLink
            ? "Attempting automatic sign-in..."
            : "Initializing..."
        }
      />
    );
  }


  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginHint || "");

  return (
    <>
      <CustomModal
        isOpen={Boolean(accountConflict)}
        type="remainder"
        icon={alertIcon}
        hideClose={true}
        header="Already Signed In"
        message={
          accountConflict
            ? `You're already signed in as ${accountConflict.existingEmail} in this browser. Please log out of that account before signing in with a different one.`
            : ""
        }
        primaryLabel="Log Out"
        confirm={async () => {
          try {
            await firebaseSignOut();
          } catch {}
          clearStoredIdToken();
          setCachedUserProfile(null);
          dispatch(setAuthorisedUser(null));
          dispatch(setIsAuthorised(false));
          setAccountConflict(null);
          setAuthUser(null);
          setCurrentUser(null);
          lastAuthKey.current = null;
        }}
      />
      <div className="login-wrapper">
        <div className="login-shell">

          <div className="form-pane">
            <div className="form-inner">

              <div className="brand-block">
                <img src={Logo} alt="Canary Logo" className="brand-logo" />
                <p className="brand-sub">Set up your credential to get started</p>
              </div>

              {emailLinkError ? (
                <div
                  style={{
                    marginBottom: "12px",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    background: "#fff3f3",
                    color: "#b00020",
                    fontSize: "0.9rem",
                  }}
                >
                  {emailLinkError}
                </div>
              ) : null}

              <div className="hint-input">
                <input
                  id="email"
                  type="email"
                  inputMode="email"
                  placeholder="Enter your email"
                  value={loginHint}
                  onChange={(e) => setLoginHint(e.target.value.trim())}
                  aria-label="Work email for single sign-on"
                  autoComplete="email"
                />
              </div>

              <button
                disabled={!isEmailValid || continueSubmitting}
                className={`cta ${isEmailValid && !continueSubmitting ? "cta-enabled" : "cta-disabled"}`}
                onClick={handleLogin}
              >
                {continueSubmitting ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="h-4 w-4 rounded-full border-2 border-black/40 border-t-black animate-spin" />
                    Redirecting...
                  </span>
                ) : (
                  "Continue"
                )}
              </button>

              {emailOtpOpen && (
                <div
                  className="email-otp-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="email-otp-title"
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setEmailOtpOpen(false);
                      setEmailOtpError("");
                    }
                  }}
                  tabIndex={-1}
                >
                  <div className="email-otp-card">
                    <div className="email-otp-header">
                      <div>
                        <h3 id="email-otp-title">Verify your email</h3>
                        <p>
                          Enter the 4-digit OTP sent to{" "}
                          <span className="email-otp-email">{emailOtpRequestedEmail}</span>
                        </p>
                      </div>
                      <button
                        type="button"
                        className="email-otp-close"
                        onClick={() => {
                          setEmailOtpOpen(false);
                          setEmailOtpError("");
                        }}
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>

                    <div className="email-otp-body">
                      <div className="email-otp-inputs">
                        {Array.from({ length: 4 }).map((_, idx) => (
                          <input
                            key={`otp-${idx}`}
                            ref={(el) => {
                              emailOtpRefs.current[idx] = el;
                            }}
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={1}
                            value={emailOtpCode[idx] || ""}
                            onChange={(e) => handleEmailOtpChange(idx, e.target.value)}
                            onKeyDown={(e) => handleEmailOtpKeyDown(idx, e)}
                            onPaste={handleEmailOtpPaste}
                            className="email-otp-input"
                          />
                        ))}
                      </div>

                      {emailOtpError ? (
                        <p className="email-otp-error">{emailOtpError}</p>
                      ) : null}

                      <div className="email-otp-actions">
                        <button
                          type="button"
                          disabled={emailOtpLoading || isVerifyingOtp}
                          onClick={() => sendEmailOtp(emailOtpRequestedEmail)}
                          className="email-otp-secondary"
                        >
                          {emailOtpLoading && !isVerifyingOtp ? "Sending..." : "Resend OTP"}
                        </button>
                        <button
                          type="button"
                          disabled={emailOtpLoading || emailOtpCode.length < 4}
                          onClick={verifyEmailOtp}
                          className="email-otp-primary"
                        >
                          {isVerifyingOtp ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="h-4 w-4 rounded-full border-2 border-black/40 border-t-black animate-spin" />
                              Verifying...
                            </span>
                          ) : "Verify OTP"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="divider">
                <span>or continue with</span>
              </div>

              <div className="social-row">
                <button
                  className="social-btn"
                  onClick={() => handleSocalLogin()}
                  aria-label="Login with Google"
                >
                  <svg className="gmark" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  <span>Login With Google</span>
                </button>
                <button
                  className="social-btn"
                  onClick={() => handleMicrosoftLogin("popup")}
                  aria-label="Login with Microsoft"
                >
                  <img className="msmark" src={Microsoft} alt="Microsoft" width="20" height="20" />
                  <span>Login with Microsoft</span>
                </button>
              </div>

              <p className="terms">
                By creating an account, you agree to our{" "}
                <a href="https://canaryai.com/terms-of-use" target="_blank" rel="noopener noreferrer">
                  Terms of Use
                </a>{" "}
                and{" "}
                <a href="https://canaryai.com/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
              </p>

              <div className="phone-auth">
                <button
                  type="button"
                  className="phone-toggle"
                  onClick={() => setPhoneAuthOpen((v) => !v)}
                >
                  Continue with phone
                </button>

                {phoneAuthOpen && (
                  <div className="phone-panel">
                    <div id="recaptcha-container" />

                    <div className="phone-input-row" ref={countryDropdownRef}>

                      <button
                        type="button"
                        className="country-trigger"
                        onClick={() => {
                          setCountryDropdownOpen((v) => !v);
                          setCountrySearch("");
                        }}
                        aria-label="Select country code"
                      >
                        <img
                          src={`https://flagcdn.com/w20/${selectedCountry.code.toLowerCase()}.png`}
                          srcSet={`https://flagcdn.com/w40/${selectedCountry.code.toLowerCase()}.png 2x`}
                          width="20"
                          height="15"
                          alt={selectedCountry.name}
                          className="country-flag-img"
                        />
                        <span className="country-dial">{selectedCountry.dialCode}</span>
                        <svg
                          className={`chevron${countryDropdownOpen ? " open" : ""}`}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          width="14"
                          height="14"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>

                      {countryDropdownOpen && (
                        <div className="country-dropdown">
                          <input
                            type="text"
                            className="country-search"
                            placeholder="Search country..."
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            autoFocus
                          />
                          <ul className="country-list">
                            {filteredCountries.length ? (
                              filteredCountries.map((c) => (
                                <li
                                  key={c.code}
                                  className={`country-item${c.code === selectedCountry.code ? " selected" : ""}`}
                                  onClick={() => {
                                    setSelectedCountry(c);
                                    setCountryDropdownOpen(false);
                                    setCountrySearch("");
                                  }}
                                >
                                  <img
                                    src={`https://flagcdn.com/w20/${c.code.toLowerCase()}.png`}
                                    srcSet={`https://flagcdn.com/w40/${c.code.toLowerCase()}.png 2x`}
                                    width="20"
                                    height="15"
                                    alt={c.name}
                                    className="country-flag-img"
                                  />
                                  <span className="country-name">{c.name}</span>
                                  <span className="country-dial muted">{c.dialCode}</span>
                                </li>
                              ))
                            ) : (
                              <li className="country-item muted">No results</li>
                            )}
                          </ul>
                        </div>
                      )}

                      <input
                        type="tel"
                        inputMode="numeric"
                        placeholder={"0".repeat(phoneRule.min)}
                        value={localPhone}
                        maxLength={phoneRule.max}
                        onChange={(e) => setLocalPhone(e.target.value.replace(/\D/g, "").slice(0, phoneRule.max))}
                        className="phone-number-input"
                      />
                    </div>

                    {phoneAuthStep === "code-sent" && (
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="Enter OTP"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value)}
                        className="phone-input"
                      />
                    )}

                    {phoneAuthError ? (
                      <p className="phone-error">{phoneAuthError}</p>
                    ) : null}

                    {phoneAuthStep === "idle" ? (
                      <button
                        type="button"
                        disabled={phoneAuthLoading}
                        onClick={handleSendOtp}
                        className="phone-cta"
                      >
                        {phoneAuthLoading ? "Sending OTP..." : "Send OTP"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={phoneAuthLoading}
                        onClick={handleVerifyOtp}
                        className="phone-cta"
                      >
                        {phoneAuthLoading ? (
                          <span className="inline-flex items-center gap-2">
                            <span className="h-4 w-4 rounded-full border-2 border-black/40 border-t-black animate-spin" />
                            Verifying...
                          </span>
                        ) : "Verify OTP"}
                      </button>
                    )}
                  </div>
                )}
              </div>

            </div>
          </div>

          <div className="art-pane">
            <img className="art-img desktop" src={login} alt="Super Admin Login" />
            <img className="art-img mobile"  src={login} alt="Super Admin Login" />
          </div>

        </div>
      </div>
    </>
  );
};

export default SuperAdminLogin;
